// Eskom PARCC — pure filter + search for the Findings register.
//
// Ported from the monolith's `filteredFindings()` (index.html): the dataset,
// compliance and anchor selects narrow by exact match (compliance against
// `finalCompliance ?? suggestedCompliance`, anchor against membership of the
// finding's anchor list), and the free-text search is a case-insensitive
// substring over the searchable fields. Task 14 also adds Category (the coarse
// dataset type) and Division selects, and broadens the search to include the
// route and matched-rule themes.
//
// Each finding is joined to its transaction by `referenceNo` so dataset/division
// (and the derived category) can be filtered on. The join is supplied by the
// caller as a `txByRef` map, so this helper does no fetching and stays pure for
// unit testing. Input order is preserved — the DataTable owns sorting.
import type { Transaction } from '../../data/types';
import type { FindingRead } from '../../lib/db';
import { datasetCategoryFor } from '../dashboard/derive';
import { isFindingLevel } from '../../domain/dashboard';
import { canonicalDivision } from '../../domain/divisions';
import { findingDisposition } from '../../domain/findings';
import { isSelfDeclared } from '../../domain/gaps';

/** Map from a transaction `referenceNo` to its transaction (the finding join). */
export type TxByRef = ReadonlyMap<string, Transaction>;

export interface FindingFilters {
  dataset: string;
  /** Coarse dataset category (Contracts / PO·LPO / Task Orders / Other). */
  category: string;
  division: string;
  /** Matched against `finalCompliance ?? suggestedCompliance`. */
  compliance: string;
  /** Matched against membership of the finding's `anchors` list. */
  anchor: string;
  /** Review state of the finding's transaction (Reviewed / Pending Review / …). */
  reviewed: string;
  /**
   * Disposition of the finding itself: '' (all), 'outstanding', 'self-declared' or
   * 'resolved'. The register defaults to 'outstanding' (Lindiwe R9) so declared and
   * resolved findings do not read as still-open work; switching this reveals them.
   */
  disposition: string;
  /** Free-text search across finding text/reference/route/rationale/rules (+ more). */
  search: string;
}

export const EMPTY_FINDING_FILTERS: FindingFilters = {
  dataset: '',
  category: '',
  division: '',
  compliance: '',
  anchor: '',
  reviewed: '',
  disposition: '',
  search: '',
};

/**
 * The review state of a finding, derived from its parent transaction's
 * `reviewStatus` (findings carry no review flag of their own; the four-state
 * status — Not Reviewed / In Progress / Pending Review / Reviewed — lives on the
 * transaction, dual-written on every working-paper save). A blank/missing status
 * reads as 'Not Reviewed'. Lindiwe feedback Jun 23 2026 (1a): a "Reviewed" filter
 * so users can separate reviewed items from those still pending review.
 */
export function deriveFindingReviewedState(tx: Transaction | undefined): string {
  const s = (tx?.reviewStatus ?? '').toString().trim();
  return s || 'Not Reviewed';
}

/** The compliance level shown/filtered: the final result, else the suggestion. */
export function effectiveFindingCompliance(f: FindingRead): string {
  return f.finalCompliance ?? f.suggestedCompliance ?? '';
}

/** True when a finding line is an actual NON-COMPLIANCE (a gap) — its effective
 *  compliance carries a risk weight (Possible IE / Policy & Procedures /
 *  Housekeeping and their self-declared variants). Compliant / Not Applicable /
 *  Pending / blank lines are not findings for the register's purpose. Lindiwe
 *  feedback Jun 2026: keep only the non-compliant lines on the Findings Register. */
export function isNonCompliantFinding(f: FindingRead): boolean {
  return isFindingLevel(effectiveFindingCompliance(f));
}

/**
 * The findings that COUNT as gaps for the register, dashboard and reports:
 * non-deleted lines whose effective compliance carries a risk weight. Compliant
 * procedure notes, Not-Applicable, Pending and blank lines are excluded so all
 * three surfaces report findings on one consistent basis. This is the single
 * definition of "reportable" — the dashboard and report aggregators
 * (breachBreakdown / findingLineCount / …) key off a finding's anchors alone and
 * trust the caller to pass only the lines that count, so every finding-count
 * surface must select through here first. Input order is preserved.
 */
export function reportableFindings(findings: readonly FindingRead[]): FindingRead[] {
  return findings.filter((f) => !f.deleted && isNonCompliantFinding(f));
}

/**
 * Lowercased haystack of the searchable fields, mirroring the monolith
 * (reference, division, dataset, finding text, rationale) and extending it per
 * Task 14 with the route and matched-rule themes.
 */
function haystack(f: FindingRead, tx: Transaction | undefined): string {
  return [
    f.referenceNo,
    f.text,
    f.rationale,
    f.route,
    ...(f.matchedRules ?? []),
    tx?.dataset,
    tx?.division,
    tx?.supplier,
  ]
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .join(' ')
    .toLowerCase();
}

/**
 * Narrow `findings` by the active filters, joining each to its transaction via
 * {@link TxByRef}. Each non-empty select narrows by exact match (dataset /
 * division / category against the joined transaction; compliance against
 * `finalCompliance ?? suggestedCompliance`; anchor against the finding's anchor
 * list); the search term narrows by case-insensitive substring across
 * {@link haystack}. Deleted findings are dropped. Input order is preserved.
 */
export function filterFindings(
  findings: readonly FindingRead[],
  txByRef: TxByRef,
  filters: FindingFilters,
): FindingRead[] {
  const q = filters.search.trim().toLowerCase();
  return findings.filter((f) => {
    if (f.deleted) return false;
    const tx = f.referenceNo ? txByRef.get(f.referenceNo) : undefined;

    if (filters.dataset && (tx?.dataset ?? '') !== filters.dataset) return false;
    if (filters.category && datasetCategoryFor(tx?.dataset) !== filters.category) return false;
    if (filters.division && canonicalDivision(tx?.division) !== filters.division) return false;
    if (filters.compliance && effectiveFindingCompliance(f) !== filters.compliance) return false;
    if (filters.anchor && !(f.anchors ?? []).includes(filters.anchor)) return false;
    if (filters.reviewed && deriveFindingReviewedState(tx) !== filters.reviewed) return false;
    // A finding on a self-declared transaction is self-declared (Lindiwe R9).
    if (filters.disposition && findingDisposition(f, tx ? isSelfDeclared(tx) : false) !== filters.disposition)
      return false;
    if (q && !haystack(f, tx).includes(q)) return false;
    return true;
  });
}

/** Sorted, de-duplicated list of the non-blank values of `pick` across `findings`. */
export function distinctFindingValues(
  findings: readonly FindingRead[],
  pick: (f: FindingRead) => string | undefined,
): string[] {
  const seen = new Set<string>();
  for (const f of findings) {
    const v = pick(f);
    if (v) seen.add(v);
  }
  return [...seen].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}
